import React, { useMemo } from 'react';
import { REFERENCE_OPTIONS } from '../../lib/calculator';
import { formatAssessmentDate } from './PatientChart';
import SafeIcon from '../../common/SafeIcon';
import { useLocale } from '../LocaleProvider';

function referenceLabel(value) {
  return REFERENCE_OPTIONS.find((option) => option.value === value)?.label || value || 'Referência não informada';
}

function orderedAssessments(assessments = []) {
  return [...assessments]
    .map((assessment, index) => ({ assessment, index }))
    .sort((left, right) => {
      const date = String(right.assessment.applied_at || '').slice(0, 10)
        .localeCompare(String(left.assessment.applied_at || '').slice(0, 10));
      if (date !== 0) return date;
      const created = String(right.assessment.created_at || '').localeCompare(String(left.assessment.created_at || ''));
      return created !== 0 ? created : right.index - left.index;
    })
    .map(({ assessment }) => assessment);
}

export { orderedAssessments };

export default function AssessmentTimeline({ assessments = [], onEdit, onDelete }) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const ordered = useMemo(() => orderedAssessments(assessments), [assessments]);

  if (!ordered.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--surface-soft)] p-6 text-center">
        <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--hover)] text-[var(--ink-2)]" aria-hidden="true">
          <SafeIcon name="Calendar" className="h-5 w-5" />
        </span>
        <p className="font-bold text-[var(--primary)]">{es ? 'No hay evaluaciones en este intervalo.' : 'Nenhuma avaliação nesse recorte.'}</p>
        <p className="mt-1 text-sm text-[var(--text-muted)]">{es ? 'Ajusta los filtros o registra una aplicación anterior.' : 'Ajuste os filtros ou registre uma aplicação anterior.'}</p>
      </div>
    );
  }

  return (
    <ol aria-label={es ? 'Historial de evaluaciones' : 'Histórico de avaliações'} className="relative border-l border-[var(--line)] pl-7">
      {ordered.map((assessment) => (
        <li key={assessment.id} className="relative pb-7 last:pb-0">
          <span aria-hidden="true" className="absolute -left-[33px] top-1.5 h-3 w-3 rounded-full border-2 border-[var(--accent)] bg-[var(--surface)]" />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <time dateTime={assessment.applied_at || undefined} className="font-display text-lg text-[var(--ink)]">{formatAssessmentDate(assessment.applied_at, es)}</time>
                <span className="font-display text-lg font-semibold text-[var(--accent)]">{assessment.score} / 30</span>
              </div>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{referenceLabel(assessment.reference_key)}</p>
              {assessment.observation && <p className="mt-2 max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-[var(--text)]">{assessment.observation}</p>}
            </div>
            <div className="flex shrink-0 gap-2">
              {onEdit && <button type="button" onClick={() => onEdit(assessment)} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-bold text-[var(--primary)] hover:bg-[var(--surface-soft)]">{es ? 'Editar' : 'Editar'}</button>}
              {onDelete && <button type="button" onClick={() => onDelete(assessment)} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-bold text-[var(--danger-text)] hover:bg-[var(--danger-bg)]">{es ? 'Eliminar' : 'Excluir'}</button>}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
